import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Minus, Plus } from "lucide-react";
import { useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import type {
  BillingInterval,
  Entitlement,
  Product,
} from "@opsbelt/contract";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import type { MeContext } from "./AppShell";

const ENTITLED = new Set(["active", "trialing", "past_due"]);

/** Pence → "£1.99" (or "£10.68"). */
function gbp(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

/** "/seat/month equivalent" for a price + interval (annual = /12). */
function perSeatMonth(pence: number, interval: BillingInterval): string {
  return interval === "year" ? gbp(Math.round(pence / 12)) : gbp(pence);
}

export function Billing() {
  const { me } = useOutletContext<MeContext>();
  const [params] = useSearchParams();
  const products = useQuery({ queryKey: ["products"], queryFn: api.products });
  const [interval, setInterval] = useState<BillingInterval>("month");

  const status = params.get("status");
  const entFor = (id: string): Entitlement | undefined =>
    me?.entitlements.find((e) => ENTITLED.has(e.status) && e.productId === id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">
            Billing
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            One account, one invoice across every tool on the belt.
          </p>
        </div>
        <BillingPortalButton />
      </div>

      {status === "success" && (
        <p className="rounded-md bg-success/10 px-3 py-2 text-sm text-success">
          Subscription confirmed — your tools are unlocked.
        </p>
      )}
      {status === "cancelled" && (
        <p className="rounded-md bg-secondary px-3 py-2 text-sm text-muted-foreground">
          Checkout cancelled. No charge was made.
        </p>
      )}

      {/* Monthly / annual toggle — applies to every paid plan below. */}
      <div className="inline-flex rounded-lg border border-border p-1">
        {(["month", "year"] as const).map((iv) => (
          <button
            key={iv}
            type="button"
            onClick={() => setInterval(iv)}
            className={
              "rounded-md px-3 py-1.5 text-sm transition " +
              (interval === iv
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground")
            }
          >
            {iv === "month" ? "Monthly" : "Annual"}
            {iv === "year" && (
              <span className="ml-1.5 font-mono text-[10px] uppercase">save</span>
            )}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {products.data?.products.map((p) => (
          <ProductCard
            key={p.id}
            product={p}
            interval={interval}
            entitlement={entFor(p.id)}
          />
        ))}
      </div>
    </div>
  );
}

function BillingPortalButton() {
  const portal = useMutation({
    mutationFn: () => api.billing.portal(),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });
  return (
    <Button variant="outline" onClick={() => portal.mutate()}>
      Manage billing
    </Button>
  );
}

function ProductCard({
  product,
  interval,
  entitlement,
}: {
  product: Product;
  interval: BillingInterval;
  entitlement: Entitlement | undefined;
}) {
  const canManage = useCanManage();
  const isAllAccess = product.id === "all_access";
  // A paid entitlement is one with seats; a free entitlement (org-wide) has none.
  const isPaid = entitlement != null && entitlement.source === "subscription";
  const pricing = product.pricing;
  const amount = pricing
    ? interval === "year"
      ? pricing.annualPence
      : pricing.monthlyPence
    : null;

  return (
    <Card>
      <CardContent className="flex h-full flex-col gap-3 p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{product.name}</h3>
          {isPaid ? (
            <Badge variant="success">{entitlement?.interval === "year" ? "annual" : "monthly"}</Badge>
          ) : product.freeMeterLimit != null ? (
            <Badge variant="muted">free</Badge>
          ) : null}
        </div>

        <p className="text-sm text-muted-foreground">
          {isAllAccess
            ? "Every tool on the belt, one price — the obvious buy once you own a few."
            : "Paste-one-command file uploads into your own S3 / Azure."}
        </p>

        {/* Free tier line */}
        {product.freeMeterLimit != null && (
          <div className="text-xs text-muted-foreground">
            Free: {product.freeMeterLimit} uploads / org / month, org-wide.
          </div>
        )}

        {/* Paid price */}
        {amount != null && pricing && (
          <div className="flex items-baseline gap-1">
            <span className="font-display text-2xl font-bold">
              {perSeatMonth(amount, interval)}
            </span>
            <span className="text-sm text-muted-foreground">/ seat / month</span>
            {interval === "year" && (
              <span className="ml-1 text-xs text-muted-foreground">
                ({gbp(amount)} / seat / year, billed yearly)
              </span>
            )}
          </div>
        )}

        {isPaid && entitlement ? (
          <PaidPlanControls product={product} entitlement={entitlement} canManage={canManage} />
        ) : (
          <SubscribeButton
            product={product}
            interval={interval}
            disabled={!canManage || amount == null}
          />
        )}
      </CardContent>
    </Card>
  );
}

function SubscribeButton({
  product,
  interval,
  disabled,
}: {
  product: Product;
  interval: BillingInterval;
  disabled: boolean;
}) {
  const [seats, setSeats] = useState(1);
  const checkout = useMutation({
    mutationFn: () => api.billing.checkout(product.id, interval, seats),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });
  const isAllAccess = product.id === "all_access";

  return (
    <div className="mt-auto space-y-2">
      <SeatStepper value={seats} onChange={setSeats} min={1} disabled={disabled} />
      <Button
        className="w-full"
        disabled={disabled || checkout.isPending}
        variant={isAllAccess ? "default" : "secondary"}
        onClick={() => checkout.mutate()}
      >
        {isAllAccess ? "Get All-Access" : `Subscribe — ${seats} seat${seats === 1 ? "" : "s"}`}
      </Button>
    </div>
  );
}

function PaidPlanControls({
  product,
  entitlement,
  canManage,
}: {
  product: Product;
  entitlement: Entitlement;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const purchased = entitlement.seats ?? 1;
  const [target, setTarget] = useState(purchased);
  const update = useMutation({
    mutationFn: (seats: number) => api.billing.updateSeats(product.id, seats),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["apps"] });
      qc.invalidateQueries({ queryKey: ["seats", product.id] });
    },
  });

  const renewal = entitlement.currentPeriodEnd
    ? new Date(entitlement.currentPeriodEnd).toLocaleDateString()
    : null;
  const changed = target !== purchased;
  const decreasing = target < purchased;

  return (
    <div className="mt-auto space-y-3">
      <div className="text-xs text-muted-foreground">
        Current plan · {purchased} seat{purchased === 1 ? "" : "s"}
        {renewal && <> · renews {renewal}</>}
      </div>

      {entitlement.pendingSeats != null && (
        <p className="rounded-md bg-secondary px-2 py-1.5 text-xs text-muted-foreground">
          Reducing to {entitlement.pendingSeats} seat
          {entitlement.pendingSeats === 1 ? "" : "s"} at next renewal.
        </p>
      )}

      {canManage && (
        <>
          <SeatStepper value={target} onChange={setTarget} min={1} disabled={update.isPending} />
          <p className="text-xs text-muted-foreground">
            {changed
              ? decreasing
                ? "Removing seats applies at the next renewal — no mid-cycle refund."
                : `Adding ${target - purchased} seat${target - purchased === 1 ? "" : "s"} now — you'll be charged the prorated difference immediately.`
              : "Use the stepper above to change the seat count, then click to apply."}
          </p>
          {update.error && (
            <p className="text-xs text-destructive">
              {update.error instanceof Error ? update.error.message : "Failed"}
            </p>
          )}
          <Button
            className="w-full"
            variant="outline"
            disabled={!changed || update.isPending}
            onClick={() => update.mutate(target)}
          >
            {changed
              ? decreasing
                ? `Schedule ${target} seats (next cycle)`
                : `Add seats now — ${target} total`
              : `Update seats (currently ${purchased})`}
          </Button>
        </>
      )}
    </div>
  );
}

function SeatStepper({
  value,
  onChange,
  min,
  disabled,
}: {
  value: number;
  onChange: (n: number) => void;
  min: number;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
        aria-label="Remove seat"
      >
        <Minus className="size-3.5" />
      </Button>
      <span className="min-w-10 text-center font-mono text-sm">{value}</span>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() => onChange(value + 1)}
        aria-label="Add seat"
      >
        <Plus className="size-3.5" />
      </Button>
      <span className="text-xs text-muted-foreground">seats</span>
    </div>
  );
}

function useCanManage(): boolean {
  const { me } = useOutletContext<MeContext>();
  return me?.org.role === "owner" || me?.org.role === "admin";
}
